
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from 'lucide-react';

interface AddCustomItemFormProps {
  existingCategories: string[];
  onAddItem: (name: string, category: string, quantity?: string, note?: string) => void;
  onCancel: () => void;
}

const DEFAULT_CATEGORIES = [
  'Produce',
  'Dairy',
  'Meat & Seafood',
  'Bakery',
  'Frozen',
  'Pantry',
  'Beverages',
  'Snacks',
  'Household',
  'Other',
];

const AddCustomItemForm: React.FC<AddCustomItemFormProps> = ({
  existingCategories,
  onAddItem,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [category, setCategory] = useState('Other');
  const [note, setNote] = useState('');

  // Merge default categories with existing ones (unique)
  const allCategories = [...new Set([...existingCategories, ...DEFAULT_CATEGORIES])].sort();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;
    
    onAddItem(
      name.trim(),
      category,
      quantity.trim() || undefined,
      note.trim() || undefined
    );
    
    // Reset form
    setName('');
    setQuantity('');
    setNote('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="item-name">Item name *</Label>
        <Input
          id="item-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Milk, Bread, Paper towels"
          autoFocus
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="item-quantity">Quantity</Label>
          <Input
            id="item-quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="e.g., 2, 1 lb"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="item-category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="item-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {allCategories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="item-note">Note (optional)</Label>
        <Input
          id="item-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g., Get the organic kind"
        />
      </div>
      
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" disabled={!name.trim()} className="flex-1">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>
    </form>
  );
};

export default AddCustomItemForm;
